import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { mapApiError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

type ErrorType = "" | "mismatch" | "duplicate" | "generic";
type Step = "form" | "code";

export function SignupPage() {
  const { t } = useTranslation();
  const { refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorType, setErrorType] = useState<ErrorType>("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>("form");
  const [code, setCode] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [orgId, setOrgId] = useState("");
  const [codeError, setCodeError] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorType("");
    if (password && confirmPassword !== password) {
      setErrorType("mismatch");
      return;
    }
    setLoading(true);
    try {
      const body: Record<string, string> = { email, org_name: orgName, name };
      if (password) body.password = password;
      const data = await api<{ org_slug?: string; org_id?: string }>("/api/v2/auth/signup", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setOrgSlug(data?.org_slug || "");
      setOrgId(data?.org_id || "");
      setStep("code");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorType("duplicate");
      } else {
        setErrorType("generic");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setCodeError("");
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const verifyBody: Record<string, string> = { email: normalizedEmail, code };
      if (orgId) verifyBody.org_id = orgId;
      await api("/api/v2/auth/verify", {
        method: "POST",
        body: JSON.stringify(verifyBody),
      });
      await refreshAuth();
      navigate(orgSlug ? `/${orgSlug}` : "/", { replace: true });
    } catch (err) {
      setCodeError(
        mapApiError(
          err,
          { 401: t("signup.invalidCode"), 429: t("signup.tooManyAttempts") },
          t("signup.genericError"),
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setCodeError("");
    try {
      const normalizedEmail = email.trim().toLowerCase();
      await api("/api/v2/auth/magic-link", {
        method: "POST",
        body: JSON.stringify({ email: normalizedEmail }),
      });
    } catch {
      setCodeError(t("signup.resendError"));
    }
  }

  if (step === "code") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t("signup.verifyTitle")}</CardTitle>
            <CardDescription>
              <Trans i18nKey="signup.codeSent" values={{ email }}>
                Enter the 6-digit code sent to <strong>{email}</strong>
              </Trans>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t("signup.code")}</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>
              {codeError && <p className="text-sm text-destructive">{codeError}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {t("signup.verifying")}
                  </>
                ) : (
                  t("signup.verify")
                )}
              </Button>
              <div className="flex justify-end text-sm">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={handleResend}
                >
                  {t("signup.resendCode")}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("signup.title")}</CardTitle>
          <CardDescription>
            {t("signup.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("signup.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgName">{t("signup.orgName")}</Label>
              <Input
                id="orgName"
                type="text"
                placeholder="Acme Engineering"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                minLength={2}
                maxLength={100}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t("signup.yourName")}</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jane Dev"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={1}
                maxLength={100}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("signup.password")}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("signup.confirmPassword")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>
            {errorType && (
              <p className="text-sm text-destructive">
                {errorType === "mismatch" && t("signup.passwordMismatch")}
                {errorType === "duplicate" && t("signup.emailAlreadyRegistered")}
                {errorType === "generic" && t("signup.genericError")}{" "}
                {errorType === "duplicate" && (
                  <Link to="/login" className="underline">
                    {t("signup.logInInstead")}
                  </Link>
                )}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t("signup.creating")}
                </>
              ) : (
                t("signup.submit")
              )}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {t("signup.alreadyHaveAccount")}{" "}
              <Link to="/login" className="underline hover:text-foreground">
                {t("signup.logIn")}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
